import { PatientStackParamList } from '@/navigations/PatientStack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useStoreActions, useStoreState } from '@/stores/store';
import { logError } from '@/utils/logHandling';
import { StackScreenProps } from '@react-navigation/stack';
import { useCallback, useEffect, useRef } from 'react';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useDialog } from '@/hooks/useDialog';
import api from '@/api/axios';
import { SelectExercise } from './SelectExercise';

export function Aufgaben({
  route,
}: StackScreenProps<
  TherapistStackParamList | PatientStackParamList,
  'Aufgaben'
>) {
  const {
    exerciseClass,
    patientId,
    setId,
    isNetworkError,
    exerciseSettings,
    isSelf = false,
    selectedPatient,
    activeSets,
    preview,
  } = route.params;

  const patientIdRef = useRef(patientId);
  const resultBuffer = useStoreState(
    (state) => state.resultsWithKey['results' + patientIdRef.current],
  );
  const unloadTherapistBuffer = useStoreActions(
    (actions) => actions.unloadTherapistBuffer,
  );
  const { showNotification } = useNotification();
  const { showDialog, removeAllDialogs } = useDialog();

  const uploadResults = useCallback(async () => {
    // used when there is an issue with the network and the therapist/patient wants to upload results later or retry upload
    api
      .post('/api/results', {
        Results: resultBuffer ?? [],
      })
      .then((result) => {
        if (result.status === 200) {
          showNotification({
            type: 'info',
            title: 'Ergebnisse hochgeladen',
            position: 'top',
          });
          unloadTherapistBuffer({ patientId: patientIdRef.current });
          removeAllDialogs();
        }
      })
      .catch((error) => {
        // Network error keeps the retry dialog open; anything else is unexpected.
        if (error?.message !== 'Network Error') {
          logError('Could not upload results', error);
        }
      });
  }, [resultBuffer, unloadTherapistBuffer, showNotification, removeAllDialogs]);

  const showUploadFailedDialog = useCallback(() => {
    showDialog({
      title: 'Upload fehlgeschlagen',
      content: 'Ergebnis-Upload fehlgeschlagen. Erneut versuchen?',
      showCancelButton: true,
      confirmAction: {
        label: 'Erneut versuchen',
        onPress: () => uploadResults(),
      },
      cancelAction: {
        label: 'Später übertragen',
        onPress: (_e, onClose) => {
          onClose?.();
          showNotification({
            type: 'info',
            title: 'Ergebnisse zwischengespeichert',
            position: 'top',
          });
        },
      },
    });
  }, [showDialog, uploadResults, showNotification]);

  useEffect(() => {
    if (isNetworkError) {
      setTimeout(() => showUploadFailedDialog(), 10);
    }
  }, [isNetworkError, showUploadFailedDialog]);

  return (
    <SelectExercise
      exerciseClass={exerciseClass}
      patientId={patientId}
      setId={setId}
      exerciseSettings={exerciseSettings}
      isSelf={isSelf}
      selectedPatient={selectedPatient}
      activeSets={activeSets}
      preview={preview}
    />
  );
}
